import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { storyPaths } from '../storyPaths.js'
import { debugFireAlarm as fireGlobalCooldown } from '../global_cooldown/globalCooldownSlice.js'
import { setDebugTimeOfDay, selectDebugTimeOfDay } from '../settings/settingsSlice.js'

const TIME_OF_DAY_LABELS = { morning: '7:00am', afternoon: '12:00pm', evening: '7:00pm', night: '12:00am' }
const TIME_OF_DAY_SLOTS = ['morning', 'afternoon', 'evening', 'night']

function formatAlarm (timestamp) {
  if (!timestamp) return '—'
  const d = new Date(timestamp)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function PathRow ({ path }) {
  const dispatch = useDispatch()
  const alarm = useSelector(path.slice.selectors.selectNextAlarmTimestamp)
  const timeOfDay = useSelector(path.slice.selectors.selectNextTimeOfDay)

  return (
    <div className='flex items-center gap-2 text-xs font-mono py-0.5'>
      <span className='w-16 truncate'>{path.label}</span>
      <span className='w-16 text-yellow-300'>{timeOfDay ?? '—'}</span>
      <span className='w-16 text-green-300'>{formatAlarm(alarm)}</span>
      <button
        onClick={() => {
          dispatch(path.slice.actions.debugFireAlarm())
          dispatch(fireGlobalCooldown())
        }}
        disabled={!alarm}
        className='px-1.5 py-0.5 bg-red-600 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded text-xs'
      >
        Fire
      </button>
    </div>
  )
}

export default function DebugPanel () {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()

  const timeOfDayByKey = useSelector((state) =>
    Object.fromEntries(storyPaths.map((path) => [path.key, path.slice.selectors.selectNextTimeOfDay(state)]))
  )
  const debugTimeOfDay = useSelector(selectDebugTimeOfDay)

  const fireForTimeOfDay = (tod) => {
    storyPaths.forEach((path) => {
      if (timeOfDayByKey[path.key] === tod) dispatch(path.slice.actions.debugFireAlarm())
    })
    dispatch(fireGlobalCooldown())
  }

  return (
    <div className='fixed bottom-2 right-2 z-[9999] font-mono text-white select-none'>
      <button
        onClick={() => setOpen(o => !o)}
        className='px-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs'
      >
        {open ? '▼ DEBUG' : '▶ DEBUG'}
      </button>

      {open && (
        <div className='mt-1 p-2 bg-gray-900 border border-gray-600 rounded text-xs'>
          <div className='flex gap-1 mb-2'>
            {TIME_OF_DAY_SLOTS.map(tod => (
              <button
                key={tod}
                onClick={() => fireForTimeOfDay(tod)}
                className='px-2 py-0.5 bg-indigo-700 hover:bg-indigo-500 rounded capitalize'
              >
                {tod} ({TIME_OF_DAY_LABELS[tod]})
              </button>
            ))}
          </div>

          <div className='flex items-center gap-1 mb-2'>
            <span className='text-gray-400'>pretend now:</span>
            {TIME_OF_DAY_SLOTS.map(tod => (
              <button
                key={tod}
                onClick={() => dispatch(setDebugTimeOfDay(debugTimeOfDay === tod ? null : tod))}
                className={`px-2 py-0.5 rounded capitalize ${debugTimeOfDay === tod ? 'bg-emerald-500' : 'bg-emerald-800 hover:bg-emerald-600'}`}
              >
                {tod}
              </button>
            ))}
            <button
              onClick={() => dispatch(setDebugTimeOfDay(null))}
              className={`px-2 py-0.5 rounded ${debugTimeOfDay === null ? 'bg-gray-500' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              real
            </button>
          </div>

          <div className='flex gap-2 text-gray-400 text-xs mb-1'>
            <span className='w-16'>path</span>
            <span className='w-16'>waiting for</span>
            <span className='w-16'>fires at</span>
          </div>

          {storyPaths.map((path) => (
            <PathRow key={path.key} path={path} />
          ))}
        </div>
      )}
    </div>
  )
}
